import React, { useEffect, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Link from "../../components/Link";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress
} from "@material-ui/core";
import Autocomplete from "@mui/material/Autocomplete";
import MuiTextField from "@mui/material/TextField";
import MuiCircularProgress from "@mui/material/CircularProgress";
import { AdminContainer } from "../../components/layouts";
import api from "../../api";
import dayjs from "dayjs";

const useStyles = makeStyles((theme) => ({
  filters: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    alignItems: "center"
  },
  filterControl: {
    minWidth: 180
  },
  summaryCard: {
    textAlign: "center",
    height: "100%"
  },
  summaryValue: {
    fontSize: "2rem",
    fontWeight: "bold",
    color: theme.palette.primary.main
  },
  summaryLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem"
  },
  successRate: {
    color: "#4caf50"
  },
  failedValue: {
    color: "#f44336"
  },
  runningValue: {
    color: "#ff9800"
  },
  section: {
    marginTop: theme.spacing(3)
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600
  }
}));

const TRIGGER_TYPES = [
  { value: "trigger.lead.created", label: "New lead added" },
  { value: "trigger.lead.updated", label: "Lead stage changed" },
  { value: "trigger.application.updated", label: "Application status change" },
  { value: "trigger.schedule", label: "Scheduled workflow" }
];

const TRIGGER_TYPE_OPTIONS = [{ value: "", label: "All triggers" }, ...TRIGGER_TYPES];

const TRIGGER_TYPE_LABELS = Object.fromEntries(
  TRIGGER_TYPES.map((t) => [t.value, t.label])
);
TRIGGER_TYPE_LABELS["unknown"] = "Unknown";

export default function WorkflowAnalyticsPage() {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const [filters, setFilters] = useState(() => {
    const params = new URLSearchParams(location.search);
    return {
      partner_id: params.get("partner_id") || "",
      trigger_type: params.get("trigger_type") || "",
      from: params.get("from") || dayjs().subtract(30, "day").format("YYYY-MM-DD"),
      to: params.get("to") || dayjs().format("YYYY-MM-DD")
    };
  });

  const [partnerOptions, setPartnerOptions] = useState([]);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(() => {
    const pid = new URLSearchParams(location.search).get("partner_id");
    return pid ? { label: `Partner ${pid}`, value: parseInt(pid) } : null;
  });

  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [byTriggerType, setByTriggerType] = useState([]);
  const [byPartner, setByPartner] = useState([]);
  const [byWorkflow, setByWorkflow] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setPartnerLoading(true);
    api
      .getPartners({
        page: 1,
        search: partnerSearch,
        status: "active",
        perPage: 10
      })
      .then((resp) => {
        setPartnerOptions(
          resp.data.items.map((item) => ({
            label: item.name,
            value: item.id
          }))
        );
        setPartnerLoading(false);
      })
      .catch(() => {
        setPartnerLoading(false);
      });
  }, [partnerSearch]);

  const buildParams = useCallback(() => {
    const params = {};
    if (filters.partner_id) params.partner_id = filters.partner_id;
    if (filters.trigger_type) params.trigger_type = filters.trigger_type;
    if (filters.from) params.from = filters.from;
    if (filters.to) params.to = filters.to;
    return params;
  }, [filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = buildParams();

    try {
      const [summaryRes, dailyRes, triggerRes, partnerRes, workflowRes] =
        await Promise.all([
          api.getWorkflowAnalyticsSummary(params).then(api.pickData),
          api.getWorkflowAnalyticsDaily(params).then(api.pickData),
          api.getWorkflowAnalyticsByTriggerType(params).then(api.pickData),
          api.getWorkflowAnalyticsByPartner(params).then(api.pickData),
          api.getWorkflowAnalyticsByWorkflow(params).then(api.pickData)
        ]);

      setSummary(summaryRes);
      setDaily(dailyRes?.daily || []);
      setByTriggerType(triggerRes?.triggerTypes || []);
      setByPartner(partnerRes?.partners || []);
      setByWorkflow(workflowRes?.workflows || []);
    } catch (err) {
      console.error("Failed to fetch workflow analytics:", err);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.partner_id) params.set("partner_id", filters.partner_id);
    if (filters.trigger_type) params.set("trigger_type", filters.trigger_type);
    if (filters.from) params.set("from", filters.from);
    if (filters.to) params.set("to", filters.to);
    const search = params.toString();
    if (search !== location.search.replace("?", "")) {
      history.replace({ search: search ? `?${search}` : "" });
    }
  }, [filters, history, location.search]);

  const handleFilterChange = (field) => (e) => {
    setFilters((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const dateRangeLabel = filters.from === filters.to
    ? dayjs(filters.from).format("MMM D, YYYY")
    : `${dayjs(filters.from).format(dayjs(filters.from).year() === dayjs(filters.to).year() ? "MMM D" : "MMM D, YYYY")} - ${dayjs(filters.to).format("MMM D, YYYY")}`;

  if (loading && !summary) {
    return (
      <AdminContainer>
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </AdminContainer>
    );
  }

  return (
    <AdminContainer>
      <Typography variant="h5" color="primary" gutterBottom>
        Workflow Analytics
      </Typography>

      {/* Filters */}
      <Box className={classes.filters}>
        <Autocomplete
          options={
            partnerLoading
              ? [{ label: "Loading...", value: null }]
              : partnerOptions
          }
          value={selectedPartner}
          onChange={(e, value) => {
            setSelectedPartner(value);
            setFilters((prev) => ({
              ...prev,
              partner_id: value ? value.value : ""
            }));
          }}
          getOptionLabel={(option) => option.label || ""}
          isOptionEqualToValue={(option, value) =>
            option.value === value.value
          }
          sx={{ minWidth: 220 }}
          renderInput={(params) => (
            <MuiTextField
              {...params}
              label="Partner"
              size="small"
              placeholder="All partners"
              InputProps={{
                ...params.InputProps,
                onChange: (e) => {
                  setPartnerSearch(e.target.value);
                },
                endAdornment: (
                  <React.Fragment>
                    {partnerLoading ? (
                      <MuiCircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                )
              }}
            />
          )}
        />
        <FormControl
          variant="outlined"
          size="small"
          className={classes.filterControl}
        >
          <InputLabel>Trigger Type</InputLabel>
          <Select
            label="Trigger Type"
            value={filters.trigger_type}
            onChange={handleFilterChange("trigger_type")}
          >
            {TRIGGER_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          className={classes.filterControl}
          label="From"
          type="date"
          variant="outlined"
          size="small"
          value={filters.from}
          onChange={handleFilterChange("from")}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          className={classes.filterControl}
          label="To"
          type="date"
          variant="outlined"
          size="small"
          value={filters.to}
          onChange={handleFilterChange("to")}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <Typography className={classes.summaryValue}>
                  {summary.totalExecutions}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Total Executions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <Typography className={classes.summaryValue}>
                  {summary.totalSuccess}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Successful
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <Typography
                  className={`${classes.summaryValue} ${classes.failedValue}`}
                >
                  {summary.totalFailed}
                </Typography>
                <Typography className={classes.summaryLabel}>Failed</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <Typography
                  className={`${classes.summaryValue} ${summary.currentlyRunning > 0 ? classes.runningValue : ""}`}
                >
                  {summary.currentlyRunning}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Running
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <Typography
                  className={`${classes.summaryValue} ${classes.successRate}`}
                >
                  {summary.successRate}%
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Success Rate
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card className={classes.summaryCard}>
              <CardContent>
                <Typography className={classes.summaryValue}>
                  {summary.activeWorkflows}
                </Typography>
                <Typography className={classes.summaryLabel}>
                  Active Workflows
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* By Trigger Type */}
      {byTriggerType.length > 0 && (
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle} color="primary">
            By Trigger Type
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Trigger Type</TableCell>
                  <TableCell align="right">Total Executions</TableCell>
                  <TableCell align="right">Success</TableCell>
                  <TableCell align="right">Failed</TableCell>
                  <TableCell align="right">Success Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byTriggerType.map((row) => (
                  <TableRow key={row.triggerNodeKind}>
                    <TableCell>
                      {TRIGGER_TYPE_LABELS[row.triggerNodeKind] ||
                        row.triggerNodeKind}
                    </TableCell>
                    <TableCell align="right">{row.total}</TableCell>
                    <TableCell align="right">{row.success}</TableCell>
                    <TableCell align="right">{row.failed}</TableCell>
                    <TableCell align="right">
                      {row.total > 0
                        ? Math.round((row.success / row.total) * 100)
                        : 0}
                      %
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Daily Trend */}
      {daily.length > 0 && (
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle} color="primary">
            Daily Executions ({dateRangeLabel})
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Total Executions</TableCell>
                  <TableCell align="right">Success</TableCell>
                  <TableCell align="right">Failed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {daily.map((row) => (
                  <TableRow key={row.date}>
                    <TableCell>
                      {dayjs(row.date).format("MMM D, YYYY")}
                    </TableCell>
                    <TableCell align="right">{row.total}</TableCell>
                    <TableCell align="right">{row.success}</TableCell>
                    <TableCell align="right">{row.failed}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* By Partner */}
      {byPartner.length > 0 && (
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle} color="primary">
            Top Partners ({dateRangeLabel})
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Partner ID</TableCell>
                  <TableCell>Partner Name</TableCell>
                  <TableCell align="right">Total Executions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byPartner.map((row) => (
                  <TableRow key={row.partnerId}>
                    <TableCell>
                      <Link to={`/partners/${row.partnerId}`}>
                        {row.partnerId}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link to={`/partners/${row.partnerId}`}>
                        {row.partnerName || "—"}
                      </Link>
                    </TableCell>
                    <TableCell align="right">{row.totalExecutions}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* By Workflow */}
      {byWorkflow.length > 0 && (
        <Box className={classes.section}>
          <Typography variant="h6" className={classes.sectionTitle} color="primary">
            Top Workflows ({dateRangeLabel})
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Workflow ID</TableCell>
                  <TableCell>Workflow Name</TableCell>
                  <TableCell>Partner ID</TableCell>
                  <TableCell>Partner Name</TableCell>
                  <TableCell align="right">Total Executions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byWorkflow.map((row) => (
                  <TableRow key={row.workflowId}>
                    <TableCell>{row.workflowId}</TableCell>
                    <TableCell>{row.workflowName || "—"}</TableCell>
                    <TableCell>
                      <Link to={`/partners/${row.partnerId}`}>
                        {row.partnerId}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link to={`/partners/${row.partnerId}`}>
                        {row.partnerName || "—"}
                      </Link>
                    </TableCell>
                    <TableCell align="right">{row.totalExecutions}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Execution Log */}
      <Box className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle} color="primary">
          Execution Log
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => history.push("/workflow-execution-log")}
        >
          Open Execution Log
        </Button>
      </Box>
    </AdminContainer>
  );
}
